import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ViewChild } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatFormField } from '@angular/material/form-field';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ArmaDate, CUtilit } from 'src/app/classes/utilit';
import {ArmaServServerMethods} from '../../classes/ArmaServServerMethods';
import {MatFormFieldAppearance} from '@angular/material/form-field/form-field';

interface IDisabledDate { day: number; month: number; year: number; }

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'armaDateTimePicker',
  templateUrl: './arma-date-time-picker.component.html',
  styleUrls: ['./arma-date-time-picker.component.css']
})
export class ArmaDateTimePickerComponent implements OnInit, OnDestroy {

  @Input('appearance') appearance: MatFormFieldAppearance = 'standard'; // le design du mat form field
  @Input('disabled') isDisabled = false;                        // Désactive tout -> input & btn selection date
  @Input('ngModelDate') ngModelDate: string | Date = '';        // databinding pour envoyer une date fonctionne avec le bananacase [()]
  @Input('value') valueInput: any = '';                         // Date
  @Input('lockDayBeforeToday') mLockSelectBeforeToday: boolean = false; // Bloque la selection de date avant la date d'aujourd'hui
  @Input('lockOpenDayAfterToday') mNbLockOpenDayAfterToday: number = 0; // Bloquage de jours apres la date du jour

  @Output('dateChange') outputOnDateChange = new EventEmitter<any>();       // evenement de retour pour indiquer que la date à changé
  @Output('ngModelDateChange') ngModelDateChange = new EventEmitter<any>(); // databinding pour recuperer une date fonctionne avec le bananacase [()]

  @ViewChild('inputDatePicker', {static: true}) mIntputDatePicker!: MatDatepickerInput<HTMLInputElement>;
  @ViewChild('picker', {static: true}) mDatePicker!: MatDatepicker<any>;
  @ViewChild('formFieldDatePicker', {static: true}) mMatFormField!: MatFormField;

  private mOnLangChange?: Subscription;
  private mArrayHoliday: IDisabledDate[] = [];      // Correspond au jours non ouvrée chargé en async depuis le calendrier d'armaor
  private mFirstDayInWeek = 1; // 1 = Lundi
  public datePickerFilter = (d: Date | null): boolean => this.canSelectDate(d || new Date()); // Appelé depuis le HTML

  constructor(private mDateAdapter: DateAdapter<Date>,
              private mTranslateService: TranslateService,
              private mArmaServMethods: ArmaServServerMethods) { }

  ngOnInit(): void {
    this.mDateAdapter.getFirstDayOfWeek = () => this.mFirstDayInWeek;
    this.mOnLangChange = this.mTranslateService.onLangChange.subscribe(this.onLangChange.bind(this), console.warn);
    this.loadNoOpenDay(CUtilit.selectedDefaultFactory).catch(console.error);
  }

  /** Called once, before the instance is destroyed. */
  ngOnDestroy(): void {
    if (this.mOnLangChange)
      this.mOnLangChange.unsubscribe();
  }

  /** Remplis l'input **/
  public setDateAsync(date: Date) {
    setTimeout(() => this.valueInput = date);
  }

  public onDateChange(event: MatDatepickerInputEvent<Date>) {
    this.valueInput = event.value; // Important la valeur de l'input n'est pas enregistrer quand on selectionne une date
    this.outputOnDateChange.emit(event);
  }

  private canSelectDate(date: Date | null | undefined): boolean {
    if (date === null || date === undefined)
      return false;
    const disabledDate = ArmaDateTimePickerComponent.toDisabledDate(date);
    // Lock si on n'est avant ajd
    if (this.mLockSelectBeforeToday && date.getTime() < new Date().getTime())
      return false;

    // Lock si on est X jour apres la date d'ajd
    const arrayLockDayAfterToday = this.getArrayLockDayAfterToday();
    if (ArmaDateTimePickerComponent.isDayIn(disabledDate, arrayLockDayAfterToday))
      return false;

    // Lock si on est pas sur un jour ouvrée
    return !(ArmaDateTimePickerComponent.isDayIn(disabledDate, this.mArrayHoliday));
  }

  public getArrayLockDayAfterToday(): IDisabledDate[] {
    const arrayLockDayAfterToday: IDisabledDate[] = [];
    const maxLoop = 100;
    let addedDay = 0;
    let loop = 0;
    const editeableDate = new Date(); // On part daujourd'hui
    while (addedDay < this.mNbLockOpenDayAfterToday && loop < maxLoop) {
      loop++;
      const disabledDate = ArmaDateTimePickerComponent.toDisabledDate(editeableDate);
      if (!ArmaDateTimePickerComponent.isDayIn(disabledDate, this.mArrayHoliday)) {
        // La date ne fait pas partie des jour ouvrée, on ajoute dans la liste des disabled day
        if (!ArmaDateTimePickerComponent.isDayIn(disabledDate, arrayLockDayAfterToday))
          arrayLockDayAfterToday.push(disabledDate);
        // Incrémente et recuperation de la date seulement si on à ajouter l'objet dans la liste
        addedDay++;
      }
      editeableDate.setDate(editeableDate.getDate() + 1); // On passe à la journée suivante
    }
    if (loop == maxLoop)
      console.warn('Reach max loop for lock day');
    return arrayLockDayAfterToday;
  }

  public async loadNoOpenDay(factoryCode: number) {
    this.mArrayHoliday.length = 0; // raz
    const result: any[] = await this.mArmaServMethods.getFactoryHollydays(factoryCode);
    result.forEach((value) => this.mArrayHoliday.push(ArmaDateTimePickerComponent.toDisabledDate(ArmaDate.toClonnedDate(value))));
  }

  private onLangChange(next: LangChangeEvent) {
    switch (next.lang) {
      case 'fr':
        this.mDateAdapter.setLocale('fr');
        this.mFirstDayInWeek = 1;
        break;
      case 'he':
        this.mDateAdapter.setLocale('he');
        this.mFirstDayInWeek = 0;
        break;
      case 'en-gb':
      default:
        this.mDateAdapter.setLocale('en-gb');
        this.mFirstDayInWeek = 0;
        break;
    }
  }

  public get isValidDate(): boolean {
    if (!this.valueInput)
      return false;
    return this.canSelectDate(new Date(this.valueInput));
  }

  private static toDisabledDate(date: Date): IDisabledDate {
    return {
      day: date.getDate(),
      month: date.getMonth(),
      year: date.getFullYear()
    };
  }

  private static isDayIn(date: IDisabledDate, arrayDate: IDisabledDate[]): boolean {
    for (const holliday of arrayDate)
      if (holliday.day == date.day && holliday.month == date.month && holliday.year == date.year)
        return true;
    return false;
  }
}
